import React, {
	memo,
	useCallback,
	useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
	Form,
	Radio
} from 'semantic-ui-react';

import DescriptionTooltip from './DescriptionTooltip';

import { log } from '../../services/utils';
import HelpModal from '../Shared/HelpModal';

const FormRadio = ( {
	align = 'left',
	errors = [],
	fieldLabel,
	forceValidation,
	helpId,
	instructions,
	name,
	onChange,
	options,
	readOnly,
	required,
	value,
	width,
	...props
} ) => {
	const isInvalid = useCallback(
		() => forceValidation
			? errors.some( error => error.error )
			: false,
		[ errors, forceValidation ]
	);

	const handleChange = useCallback( (
		e,
		{
			name,
			value
		}
	) => {
		onChange( {
			target: {
				name,
				value
			}
		} );
	}, [ onChange ] );

	const readOnlyStyle = useMemo( () => ( { pointerEvents: 'none' } ), [] );

	const mapOptions = useCallback( () => readOnly
		? <p style={{ margin: 0, height: '17.14px' }}>{ options.find( option => value === option.value ).text }</p>
		: options.map( option => (
			<Form.Field
				inline
				key={ option.text }
			>
				<Radio
					checked={ value === option.value }
					label={ option.text }
					name={ name }
					onChange={ handleChange }
					readOnly={ readOnly }
					style={ { margin: '0 auto', ...readOnly && readOnlyStyle } }
					value={ option.value }
					{ ...props }
				/>
			</Form.Field>
		) ), [ handleChange, name, options, props, readOnly, readOnlyStyle, value ] );

	const alignment = useMemo(
		() => align === 'right'
			? { marginLeft: 'auto' }
			: null,
		[ align ]
	);

	return (
		<>
			{ log( `- ${ name } select` ) }
			<Form.Field
				error={ isInvalid() }
				required={ required }
				style={ {
					...alignment,
					display: 'flex',
					flexDirection: 'column'
				} }
				width={ width }
			>
				<label>
					{ helpId
						? <HelpModal
							id={ helpId }
							triggerPosition="left"
							triggerSize="small"
						/>
						: null
					}
					{ fieldLabel }
					{ instructions
						? <>&nbsp;&nbsp;<DescriptionTooltip text={ instructions } /></>
						: null
					}</label>
				<Form.Group
					inline
					style={ {
						marginBottom: 0,
						flexGrow: 1
					} }
				>
					{ mapOptions() }
				</Form.Group>
			</Form.Field>
		</>
	);
};

FormRadio.propTypes = {
	align: PropTypes.string,
	errors: PropTypes.arrayOf( PropTypes.shape( {
		error: PropTypes.bool,
		message: PropTypes.node
	} ) ),
	fieldLabel: PropTypes.node.isRequired,
	forceValidation: PropTypes.bool,
	helpId: PropTypes.number,
	instructions: PropTypes.string,
	name: PropTypes.string.isRequired,
	onChange: PropTypes.func,
	options: PropTypes.arrayOf( PropTypes.object ).isRequired,
	readOnly: PropTypes.bool,
	required: PropTypes.bool,
	search: PropTypes.bool,
	value: PropTypes.oneOfType( [
		PropTypes.string,
		PropTypes.number
	] ).isRequired,
	width: PropTypes.number
};

export default memo( FormRadio );
